import { readApiJson } from '../utils/readApiJson';

const API_BASE_URL = '/api/prisme';

export type TechPrimeFollowupStatus = 'À planifier' | 'En cours' | 'En attente client' | 'À surveiller' | 'Complété' | 'Bloqué';
export type TechPrimeFollowupPriority = 'Critique' | 'Haute' | 'Moyenne' | 'Basse';
export type TechPrimeFollowupColor = 'Normal' | 'Jaune' | 'Rouge';

export interface TechPrimeFollowupHistory {
  id: number;
  followup_id: number;
  event_type: string;
  actor_email?: string;
  created_at: string;
  snapshot?: Record<string, unknown>;
}

export interface TechPrimeFollowup {
  id?: number;
  client_id: number | '';
  technicien_id?: number | '';
  titre: string;
  statut: TechPrimeFollowupStatus;
  priorite: TechPrimeFollowupPriority;
  couleur_source: TechPrimeFollowupColor;
  comite_ti_statut?: string;
  plan_directeur?: string;
  projets_a_venir?: string;
  note_interne?: string;
  derniere_visite?: string;
  prochain_suivi?: string | null;
  canal?: string;
  destinataires?: string;
  resume_suivi?: string;
  actions_bz?: string;
  attentes_client?: string;
  decision_requise?: string;
  dernier_courriel_at?: string | null;
  archived?: boolean;
  archived_at?: string | null;
  client_nom?: string;
  technicien_nom?: string;
  technicien_email?: string;
  jours_avant_suivi?: number | null;
  created_at?: string;
  updated_at?: string;
  history?: TechPrimeFollowupHistory[];
}

export interface TechPrimeFollowupEmailDraft {
  to: string;
  subject: string;
  html: string;
}

export interface TechPrimeFollowupAdminConfig {
  availableTechnicienIds: number[];
  hasAvailableTechnicienConfig: boolean;
  assignments: Array<{
    client_id: number;
    technicien_id: number | null;
    technicien_ids?: number[];
  }>;
}

export const techPrimeFollowupStatuses: TechPrimeFollowupStatus[] = ['À planifier', 'En cours', 'En attente client', 'À surveiller', 'Complété', 'Bloqué'];
export const techPrimeFollowupPriorities: TechPrimeFollowupPriority[] = ['Critique', 'Haute', 'Moyenne', 'Basse'];
export const techPrimeFollowupColors: TechPrimeFollowupColor[] = ['Normal', 'Jaune', 'Rouge'];
export const comiteTiStatuses = ['Oui', 'Non', 'À proposer', 'Proposé en attente', 'À voir', 'Standby', 'Adressé', 'Vide'];

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {}),
    },
  });
  const data = await readApiJson<T | { error?: string }>(res);
  if (!res.ok) throw new Error((data as { error?: string })?.error || 'Erreur API suivis Tech Prime');
  return data as T;
}

export function emptyTechPrimeFollowup(): TechPrimeFollowup {
  return {
    client_id: '',
    technicien_id: '',
    titre: 'Suivi client Tech Prime',
    statut: 'À planifier',
    priorite: 'Moyenne',
    couleur_source: 'Normal',
    comite_ti_statut: '',
    plan_directeur: '',
    projets_a_venir: '',
    note_interne: '',
    derniere_visite: '',
    prochain_suivi: null,
    canal: '',
    destinataires: '',
    resume_suivi: '',
    actions_bz: '',
    attentes_client: '',
    decision_requise: '',
  };
}

export async function listTechPrimeFollowups(filters: Record<string, string | number | boolean | undefined> = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') params.set(key, String(value));
  });
  return request<TechPrimeFollowup[]>(`/tech-prime-followups${params.toString() ? `?${params}` : ''}`);
}

export async function getTechPrimeFollowup(id: number) {
  return request<TechPrimeFollowup>(`/tech-prime-followups/${id}`);
}

export async function createTechPrimeFollowup(followup: TechPrimeFollowup) {
  return request<TechPrimeFollowup>('/tech-prime-followups', {
    method: 'POST',
    body: JSON.stringify({ followup }),
  });
}

export async function updateTechPrimeFollowup(id: number, followup: TechPrimeFollowup) {
  return request<TechPrimeFollowup>(`/tech-prime-followups/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ followup }),
  });
}

export async function archiveTechPrimeFollowup(id: number) {
  return request<{ success: boolean }>(`/tech-prime-followups/${id}`, { method: 'DELETE' });
}

export async function restoreTechPrimeFollowup(id: number) {
  return request<TechPrimeFollowup>(`/tech-prime-followups/${id}/restore`, { method: 'POST' });
}

export async function getTechPrimeFollowupEmailDraft(id: number, overrides: Partial<TechPrimeFollowupEmailDraft> = {}) {
  return request<TechPrimeFollowupEmailDraft>(`/tech-prime-followups/${id}/email-draft`, {
    method: 'POST',
    body: JSON.stringify(overrides),
  });
}

export async function sendTechPrimeFollowupEmail(id: number, draft: TechPrimeFollowupEmailDraft) {
  return request<{ success: boolean; message: string; followup: TechPrimeFollowup }>(`/tech-prime-followups/${id}/send-email`, {
    method: 'POST',
    body: JSON.stringify(draft),
  });
}

export async function getTechPrimeFollowupAdminConfig() {
  return request<TechPrimeFollowupAdminConfig>('/tech-prime-followups/admin-config');
}

export async function saveTechPrimeFollowupAdminConfig(config: Pick<TechPrimeFollowupAdminConfig, 'availableTechnicienIds' | 'assignments'>) {
  return request<TechPrimeFollowupAdminConfig>('/tech-prime-followups/admin-config', {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}
